<script setup lang="ts">
import { Vue3Lottie } from 'vue3-lottie';
import { useGiratudoStore } from '@/stores/apostatudo/giratudo';
import { useGiratudo } from '~/composables/apostatudo/use-giratudo';
import { getAssetsUrlForCDN } from '@/utils/template';
import GiratudoTablePlacar from '@/components/apostatudo/molecules/GiratudoTablePlacar.vue';
import GiratudoTableMeusPremios from '@/components/apostatudo/molecules/GiratudoTableMeusPremios.vue';
import GiratudoTableHistorico from '@/components/apostatudo/molecules/GiratudoTableHistorico.vue';
import celebrationAnimation from '@/assets/json/lottie/success_celebration.json';

const giratudoStore = useGiratudoStore();
const {
  fetchStatus,
  participate,
  fetchLeaderboard,
  fetchHistory,
  fetchPrizes,
} = useGiratudo();

const frameUrl = getAssetsUrlForCDN('/assets/images/giratudo/frame.png');
const btnGirarUrl = getAssetsUrlForCDN('/assets/images/giratudo/btn-girar.png');
const btnGirarOffUrl = getAssetsUrlForCDN(
  '/assets/images/giratudo/btn-girar-off.png',
);

const btnFirstDepositUrl = getAssetsUrlForCDN(
  '/assets/images/giratudo/btn-first-deposit.png',
);

const {
  isOpen,
  raffleActive,
  eligible,
  alreadyParticipated,
  statusLoading,
  participateError,
  raffleEndTime,
  participationEnabled,
} = storeToRefs(giratudoStore);

const { close, open } = giratudoStore;
const { isMobile, isDesktop } = useLayout();

// Trava o scroll do fundo enquanto o Giratudo está aberto.
useBodyScrollLock(isOpen);

// Reabre o Giratudo após fechar o modal de depósito (apenas se estava aberto antes)
const layoutStore = useLayoutStore();
const { modalType } = storeToRefs(layoutStore);
let lastDepositModalOpen = false;

watch(modalType, async (type) => {
  if (type === 'deposit') {
    lastDepositModalOpen = isOpen.value;
  } else if (type === 'closed' && lastDepositModalOpen) {
    lastDepositModalOpen = false;
    open();
    await fetchStatus();
  } else {
    lastDepositModalOpen = false;
  }
});

// Tabs para mobile
const activeTab = ref<'placar' | 'meus-premios' | 'historico'>('placar');

const tabs = [
  { id: 'placar' as const, label: 'Placar' },
  { id: 'meus-premios' as const, label: 'Meus prêmios' },
  { id: 'historico' as const, label: 'Histórico' },
];

// Números do sorteio — cada dígito separado para animar individualmente
const digits = ref([0, 0, 0, 0, 0]);
const isSpinning = ref(false);
const showCelebration = ref(false);

// ── Layout adaptativo desktop: escala tabelas para sempre caber na tela ──
const headerRef = ref<HTMLElement | null>(null);
const tablesSectionRef = ref<HTMLElement | null>(null);
// Altura mínima necessária para as tabelas mostrarem 10 linhas + sua posição + paginação
const TABLE_MIN_HEIGHT = 540;

const desktopTableScale = ref(1);

function recalcTableScale() {
  if (!headerRef.value) return;

  const vh = window.innerHeight;
  const headerH = headerRef.value.getBoundingClientRect().height;
  const available = vh - headerH;

  if (available < TABLE_MIN_HEIGHT) {
    desktopTableScale.value = Math.max(0.5, available / TABLE_MIN_HEIGHT);
  } else {
    desktopTableScale.value = 1;
  }
}

let headerResizeObserver: ResizeObserver | null = null;

function startScaleObserver() {
  nextTick(() => {
    recalcTableScale();
    window.addEventListener('resize', recalcTableScale);

    if (headerRef.value) {
      headerResizeObserver = new ResizeObserver(recalcTableScale);
      headerResizeObserver.observe(headerRef.value);
    }
  });
}

function stopScaleObserver() {
  window.removeEventListener('resize', recalcTableScale);
  headerResizeObserver?.disconnect();
  headerResizeObserver = null;
  desktopTableScale.value = 1;
}

watch(isOpen, (open) => {
  if (open && !isMobile.value) {
    startScaleObserver();
  } else {
    stopScaleObserver();
  }
});

onUnmounted(() => {
  stopScaleObserver();
});

// Timer próximo ciclo — conta regressiva até a próxima hora cheia
const nextCycleTimer = ref('00:00:00');
let timerInterval: ReturnType<typeof setInterval> | null = null;
let isRefreshingStatus = false;
let isRefreshingTables = false;
let lastStatusRefreshTimestamp = 0;
let lastTableRefreshTimestamp = 0;
let lastSeenHour = -1;
const REFRESH_COOLDOWN_MS = 30_000;

async function updateTimer() {
  const now = new Date();
  const currentHour = now.getHours();
  const endRaw = raffleEndTime.value;
  let end = endRaw ? new Date(endRaw) : null;

  // Se não tem end time ou o end time já passou, usa a próxima hora cheia
  if (!end || end.getTime() <= now.getTime()) {
    const nextHour = new Date(now);

    nextHour.setHours(nextHour.getHours() + 1, 0, 0, 0);
    end = nextHour;
  }

  const diff = Math.max(0, end.getTime() - now.getTime());
  const hours = Math.floor(diff / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);

  nextCycleTimer.value = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

  const nowMs = Date.now();
  const hourChanged = lastSeenHour !== -1 && lastSeenHour !== currentHour;

  if (lastSeenHour === -1) {
    lastSeenHour = currentHour;
  }

  // Ao bater o countdown: só reseta o status (para atualizar o raffleEndTime e reiniciar o contador)
  if (
    diff < 1000 &&
    !isRefreshingStatus &&
    nowMs - lastStatusRefreshTimestamp > REFRESH_COOLDOWN_MS
  ) {
    isRefreshingStatus = true;
    lastStatusRefreshTimestamp = nowMs;
    try {
      await fetchStatus();
    } finally {
      isRefreshingStatus = false;
    }
  }

  // Ao virar a hora: atualiza as tabelas com cooldown independente
  if (hourChanged) {
    lastSeenHour = currentHour;
    if (
      !isRefreshingTables &&
      nowMs - lastTableRefreshTimestamp > REFRESH_COOLDOWN_MS
    ) {
      isRefreshingTables = true;
      lastTableRefreshTimestamp = nowMs;
      placarPage.value = 1;
      meusPremiosPage.value = 1;
      historicoPage.value = 1;
      try {
        await fetchAllTables();
      } finally {
        isRefreshingTables = false;
      }
    }
  }
}

function startTimer() {
  updateTimer();
  timerInterval = setInterval(() => {
    updateTimer().catch(() => {});
  }, 1000);
}

function stopTimer() {
  if (timerInterval) {
    clearInterval(timerInterval);
    timerInterval = null;
  }
  isRefreshingStatus = false;
  isRefreshingTables = false;
  lastStatusRefreshTimestamp = 0;
  lastTableRefreshTimestamp = 0;
  lastSeenHour = -1;
}

// Inicia/para o timer e busca status + tabelas conforme o modal abre/fecha
onMounted(async () => {
  if (isOpen.value) {
    preloadSounds();
    await fetchStatus();
    fetchAllTables();
    startTimer();
    if (!isMobile.value) startScaleObserver();
  }
});

watch(isOpen, async (open) => {
  if (open) {
    preloadSounds();
    // Reseta flag de decode para permitir re-decode se necessário
    soundsDecoded = false;
    placarPage.value = 1;
    meusPremiosPage.value = 1;
    historicoPage.value = 1;
    await fetchStatus();
    fetchAllTables();
    startTimer();
  } else {
    stopTimer();
    clearSpinTimers();
    soundsDecoded = false; // iOS pode suspender o AudioContext entre sessões
  }
});

// Paginação — controlada pelo servidor
const placarPage = ref(1);
const meusPremiosPage = ref(1);
const historicoPage = ref(1);

// ── Store refs para dados das tabelas ──
const {
  leaderboard,
  leaderboardLoading: placarLoading,
  history: historyData,
  historyLoading: historicoLoading,
  prizes: prizesData,
  prizesLoading: meusPremiosLoading,
} = storeToRefs(giratudoStore);

const placarData = computed(() => leaderboard.value.data);
const placarTotalPages = computed(() => leaderboard.value.totalPages);
const placarUserPosition = computed(() => leaderboard.value.userPosition);

const meusPremiosData = computed(() => prizesData.value.data);
const meusPremiosTotalPages = computed(() => prizesData.value.totalPages);

const historicoData = computed(() => historyData.value.data);
const historicoTotalPages = computed(() => historyData.value.totalPages);

// ── Buscar todas as tabelas ──
async function fetchAllTables() {
  await Promise.all([
    fetchLeaderboard(placarPage.value),
    fetchHistory(historicoPage.value),
    fetchPrizes(meusPremiosPage.value),
  ]);
}

// ── Reset página ao trocar de tab ──
watch(activeTab, (tab) => {
  if (tab === 'placar') placarPage.value = 1;
  else if (tab === 'meus-premios') meusPremiosPage.value = 1;
  else if (tab === 'historico') historicoPage.value = 1;
});

// ── Watchers de paginação — busca nova página ao trocar ──
watch(placarPage, (page) => fetchLeaderboard(page));
watch(meusPremiosPage, (page) => fetchPrizes(page));
watch(historicoPage, (page) => fetchHistory(page));

// ── Sons da roleta (Web Audio API — funciona em desktop + iPhone) ──
// Estratégia: baixa os ArrayBuffers crus no mount (não precisa de AudioContext),
// e só cria o AudioContext + decodifica no primeiro gesto do usuário.
const SOUND_TICK = getAssetsUrlForCDN('/assets/sounds/tick.wav');
const SOUND_LAND = getAssetsUrlForCDN('/assets/sounds/land.wav');
const SOUND_CELEBRATION = getAssetsUrlForCDN('/assets/sounds/celebration.wav');

let audioCtx: AudioContext | null = null;
const rawBuffers = new Map<string, ArrayBuffer>();
const audioBuffers = new Map<string, AudioBuffer>();
let soundsDecoded = false;

function getAudioContext(): AudioContext {
  if (!audioCtx) {
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;

    audioCtx = new Ctx();
  }

  return audioCtx;
}

/** Baixa os arquivos de som como ArrayBuffer (não precisa de AudioContext). */
function preloadSounds() {
  const urls = [SOUND_TICK, SOUND_LAND, SOUND_CELEBRATION];

  urls.forEach((url) => {
    if (rawBuffers.has(url)) return;

    fetch(url)
      .then((res) => res.arrayBuffer())
      .then((buf) => rawBuffers.set(url, buf))
      .catch(() => {});
  });
}

/** Decodifica os ArrayBuffers já baixados em AudioBuffers jogáveis. */
async function decodeBuffers() {
  const ctx = getAudioContext();
  const urls = [SOUND_TICK, SOUND_LAND, SOUND_CELEBRATION];

  await Promise.all(
    urls.map(async (url) => {
      if (audioBuffers.has(url)) return;

      let raw = rawBuffers.get(url);

      if (!raw) {
        try {
          const res = await fetch(url);

          raw = await res.arrayBuffer();
          rawBuffers.set(url, raw);
        } catch {
          return;
        }
      }
      try {
        const decoded = await ctx.decodeAudioData(raw.slice(0));

        audioBuffers.set(url, decoded);
      } catch {
        /* ignore */
      }
    }),
  );
  soundsDecoded = true;
}

/**
 * Desbloqueia o AudioContext no iOS — DEVE ser chamado SINCRONAMENTE
 * dentro do handler de click/touch, ANTES de qualquer await.
 * No iOS Safari, o AudioContext só é desbloqueado se a criação +
 * resume + play acontecerem na mesma call stack síncrona do gesto.
 */
function touchAudioContext() {
  const ctx = getAudioContext();

  // Toca buffer silencioso SINCRONAMENTE — isso é o que desbloqueia no iOS
  const silent = ctx.createBuffer(1, 1, 22050);
  const src = ctx.createBufferSource();

  src.buffer = silent;
  src.connect(ctx.destination);
  src.start(0);

  // Resume também de forma síncrona (a promise resolve depois, mas o call já conta)
  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }
}

/** Garante que os sons estejam decodificados (pode ser chamado após await). */
async function ensureSoundsDecoded() {
  if (!soundsDecoded) {
    await decodeBuffers();
  }
}

function playSound(url: string) {
  const ctx = getAudioContext();

  // Garante que o contexto esteja ativo (browsers podem suspender)
  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }

  const buffer = audioBuffers.get(url);

  if (!buffer) return;

  const src = ctx.createBufferSource();

  src.buffer = buffer;
  src.connect(ctx.destination);
  src.start(0);
}

function playLandSound() {
  playSound(SOUND_LAND);
}

let spinTickInterval: ReturnType<typeof setTimeout> | null = null;

function startSpinSound() {
  let delay = 80;
  const maxDelay = 300;
  const deceleration = 1.08;

  function tick() {
    playSound(SOUND_TICK);
    delay = Math.min(delay * deceleration, maxDelay);
    spinTickInterval = setTimeout(tick, delay);
  }

  tick();
}

function stopSpinSound() {
  if (spinTickInterval) {
    clearTimeout(spinTickInterval);
    spinTickInterval = null;
  }
}

function playCelebrationSound() {
  playSound(SOUND_CELEBRATION);
}

// ── Animação slot-machine premium ──
// Direita → esquerda, 1 dígito por vez, com desaceleração real + punch ao parar
const spinTimeouts: ReturnType<typeof setTimeout>[] = [];
const spinIntervals: ReturnType<typeof setInterval>[] = [];
const landed = ref<boolean[]>([false, false, false, false, false]); // controla glow do dígito revelado

function clearSpinTimers() {
  spinTimeouts.forEach(clearTimeout);
  spinTimeouts.length = 0;
  spinIntervals.forEach(clearInterval);
  spinIntervals.length = 0;
  stopSpinSound();
}

/**
 * Gira um dígito com desaceleração real (ease-out):
 * - Começa rápido (30ms entre ticks)
 * - Vai ficando lento até ~160ms no final
 * - Para exatamente no target
 * - Dispara punch CSS ao parar
 */
function spinAndStop(digitIdx: number, target: number, onDone: () => void) {
  const FAST_MS = 30; // velocidade inicial
  const SLOW_MS = 160; // velocidade final (quase parando)
  const TOTAL_TICKS = 25; // quantidade total de trocas
  const offset = (target - (digits.value[digitIdx] ?? 0) + 10) % 10;
  const totalTicks = TOTAL_TICKS + offset;

  let tick = 0;

  function scheduleNext() {
    // Ease-out: começa rápido, vai ficando lento
    const progress = tick / totalTicks; // 0→1
    const eased = progress * progress; // quadrático
    const delay = FAST_MS + (SLOW_MS - FAST_MS) * eased;

    const id = setTimeout(() => {
      tick++;
      digits.value[digitIdx] = ((digits.value[digitIdx] ?? 0) + 1) % 10;

      if (tick >= totalTicks) {
        digits.value[digitIdx] = target;
        landed.value[digitIdx] = true;
        playLandSound();
        onDone();
      } else {
        scheduleNext();
      }
    }, delay);

    spinTimeouts.push(id);
  }

  scheduleNext();
}

async function handleSpin() {
  if (isSpinning.value) return;

  // SYNC: desbloqueia AudioContext no gesto do usuário (obrigatório iOS)
  // Deve ser ANTES de qualquer await para manter a call stack do gesto
  touchAudioContext();

  // ASYNC: garante que os sons estejam decodificados
  await ensureSoundsDecoded();

  isSpinning.value = true;
  clearSpinTimers();
  digits.value = [0, 0, 0, 0, 0];
  landed.value = [false, false, false, false, false];

  // Chama a API real — aguarda o ticket_number
  const ticketNumber = await participate();

  if (!ticketNumber) {
    isSpinning.value = false;

    return;
  }

  const raw = ticketNumber.replace(/\D/g, '').padStart(5, '0').slice(-5);
  const finalResult = raw.split('').map(Number);

  // Direita pra esquerda: 4, 3, 2, 1, 0
  const revealOrder = [4, 3, 2, 1, 0];
  const DELAY_BETWEEN = 800; // 0.8s entre cada — ritmo mais fluido

  // Inicia som de roleta girando
  startSpinSound();

  revealOrder.forEach((digitIdx, step) => {
    const isLast = step === revealOrder.length - 1;
    const targetDigit = finalResult[digitIdx] ?? 0;

    const timeoutId = setTimeout(() => {
      digits.value[digitIdx] = 0;

      spinAndStop(digitIdx, targetDigit, () => {
        if (isLast) {
          stopSpinSound();
          isSpinning.value = false;
          showCelebration.value = true;
          playCelebrationSound();
          setTimeout(() => {
            showCelebration.value = false;
          }, 1600);
          fetchAllTables();
        }
      });
    }, step * DELAY_BETWEEN);

    spinTimeouts.push(timeoutId);
  });
}

// Limpa ao desmontar
onBeforeUnmount(() => {
  clearSpinTimers();
  stopTimer();
  if (audioCtx && audioCtx.state !== 'closed') {
    audioCtx.close().catch(() => {});
    audioCtx = null;
  }
  soundsDecoded = false;
  rawBuffers.clear();
  audioBuffers.clear();
});

const showInfoDropdown = ref(false);
const infoDropdownRef = ref<HTMLElement | null>(null);
const infoBtnRef = ref<HTMLElement | null>(null);

function toggleInfo() {
  showInfoDropdown.value = !showInfoDropdown.value;
}

function closeInfoDropdown() {
  showInfoDropdown.value = false;
}

function handleClickOutsideInfo(e: MouseEvent) {
  if (!showInfoDropdown.value) return;

  const target = e.target as Node;

  if (
    infoDropdownRef.value?.contains(target) ||
    infoBtnRef.value?.contains(target)
  )
    return;
  showInfoDropdown.value = false;
}

onMounted(() => {
  document.addEventListener('click', handleClickOutsideInfo, true);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutsideInfo, true);
});

function handleClose() {
  closeInfoDropdown();
  close();
}

const { openDepositModal } = usePayments();

function handleFirstDeposit() {
  openDepositModal();
}
</script>

<template>
  <Teleport to="body">
    <HeadlessTransitionRoot
      as="template"
      :show="isOpen"
    >
      <div
        class="fixed inset-0 z-[999] flex justify-center"
        :class="[
          !participationEnabled
            ? 'items-center'
            : isMobile
              ? 'items-center'
              : 'items-start overflow-y-auto',
        ]"
        @click.self="handleClose"
      >
        <!-- Backdrop -->
        <HeadlessTransitionChild
          enter="transition-opacity duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="transition-opacity duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div
            class="fixed inset-0 bg-[#000000]/60 backdrop-blur-md"
            @click="handleClose"
          />
        </HeadlessTransitionChild>

        <!-- Maintenance Modal (kill switch OFF) -->
        <HeadlessTransitionChild
          v-if="!participationEnabled"
          class="relative z-10 w-full"
          :class="[isMobile ? 'max-w-[360px] px-4' : 'max-w-[560px] mx-auto']"
          enter="transition-all duration-300"
          enter-from="opacity-0 scale-95"
          enter-to="opacity-100 scale-100"
          leave="transition-all duration-200"
          leave-from="opacity-100 scale-100"
          leave-to="opacity-0 scale-95"
        >
          <div
            class="relative flex flex-col items-center overflow-hidden rounded-2xl bg-transparent select-none px-4 py-6"
          >
            <button
              class="absolute top-3 right-3 z-20 flex h-8 w-8 items-center justify-center rounded-full bg-bg-primary !text-[#ffffff] transition hover:bg-bg-primary/80"
              @click="handleClose"
            >
              <NuxtIcon
                class="text-lg"
                name="close"
              />
            </button>

            <div class="relative flex flex-col items-center mb-4">
              <div class="relative z-10 w-[280px] lg:w-[400px]">
                <img
                  alt="Giratudo"
                  class="w-full h-auto pointer-events-none select-none"
                  draggable="false"
                  :src="frameUrl"
                />
                <div
                  class="absolute inset-0 flex items-center justify-center pt-[10%]"
                >
                  <NuxtIcon
                    class="maintenance-icon text-[#62eafb]"
                    name="clock-giratudo"
                    :style="{ fontSize: isMobile ? '56px' : '80px' }"
                  />
                </div>
              </div>
            </div>

            <div class="relative z-10 flex flex-col items-center text-center">
              <h2
                class="!text-[#ffffff] mb-2"
                :style="{
                  fontFamily: '\'Plus Jakarta Sans\', sans-serif',
                  fontSize: isMobile ? '20px' : '24px',
                  fontWeight: 800,
                  lineHeight: '1.2',
                }"
              >
                Giratudo em manutenção
              </h2>
              <p
                class="!text-[#ffffff]/70 max-w-[420px]"
                :style="{
                  fontFamily: '\'Plus Jakarta Sans\', sans-serif',
                  fontSize: isMobile ? '14px' : '15px',
                  fontWeight: 500,
                  lineHeight: '1.5',
                }"
              >
                Estamos trabalhando para melhorar sua experiência. Em breve você
                poderá girar a roleta novamente.
              </p>
            </div>
          </div>
        </HeadlessTransitionChild>

        <!-- Modal Content -->
        <HeadlessTransitionChild
          v-else
          class="relative z-10 w-full"
          :class="[isMobile ? 'h-dvh max-h-dvh' : 'max-w-[1200px] mx-auto']"
          enter="transition-all duration-300"
          enter-from="opacity-0 scale-95"
          enter-to="opacity-100 scale-100"
          leave="transition-all duration-200"
          leave-from="opacity-100 scale-100"
          leave-to="opacity-0 scale-95"
        >
          <div
            class="relative flex flex-col overflow-hidden rounded-none lg:rounded-xl bg-transparent select-none"
            :class="[isMobile ? 'h-dvh' : '']"
          >
            <!-- Header fixo (timer + frame + botão) -->
            <div
              ref="headerRef"
              class="shrink-0"
            >
              <div class="flex flex-col items-center px-3 pt-3 pb-2">
                <!-- Próximo Ciclo Timer + Info + Close Button -->
                <div class="flex items-center w-full mb-3">
                  <!-- Mobile: botão ? à esquerda do timer -->
                  <div class="flex-1 flex justify-start">
                    <div
                      v-if="isMobile"
                      class="relative"
                    >
                      <button
                        ref="infoBtnRef"
                        class="flex h-8 w-8 items-center justify-center rounded-full bg-bg-primary !text-[#ffffff] text-lg font-bold transition hover:bg-bg-primary/80"
                        @click.stop="toggleInfo"
                      >
                        ?
                      </button>
                      <!-- Dropdown info mobile -->
                      <div
                        v-if="showInfoDropdown"
                        ref="infoDropdownRef"
                        class="absolute left-0 top-10 z-50 w-[280px] rounded-xl p-4 text-sm text-white leading-relaxed shadow-lg bg-bg-primary border border-white/10"
                      >
                        <p
                          >A cada 1 hora, você ganha uma nova chance de girar a
                          roleta!</p
                        >
                        <p class="mt-2">
                          Cada giro sorteia um número aleatório — quanto maior o
                          número, melhor a sua colocação no ranking.
                        </p>
                        <p class="mt-2">
                          Ao final de cada hora, os 10 maiores números do
                          período são os vencedores do prêmio 🎁
                        </p>
                      </div>
                    </div>
                  </div>

                  <div
                    class="flex items-center gap-2 px-4 h-8"
                    style="
                      border-radius: 157.5px;
                      border: 1.125px solid #122041;
                      background: #000717;
                    "
                  >
                    <span
                      class="!text-[#ffffff]"
                      :style="{
                        fontFamily: '\'Plus Jakarta Sans\', sans-serif',
                        fontSize: isMobile ? '16px' : '18px',
                        fontWeight: 700,
                        lineHeight: '22.5px',
                      }"
                      >PRÓXIMO CICLO</span
                    >
                    <span class="flex items-center gap-2">
                      <NuxtIcon
                        class="text-[#62eafb]"
                        name="clock-giratudo"
                        :style="{
                          fontSize: isMobile ? '18px' : '22px',
                          transform: 'none',
                        }"
                      />
                      <span
                        class="!text-[#62eafb] inline-block text-center"
                        :style="{
                          fontFamily: '\'Plus Jakarta Sans\', sans-serif',
                          fontSize: isMobile ? '16px' : '18px',
                          fontWeight: 700,
                          lineHeight: '22.5px',
                          fontVariantNumeric: 'tabular-nums',
                          minWidth: '5ch',
                        }"
                        >{{ nextCycleTimer }}</span
                      >
                    </span>
                  </div>

                  <!-- Desktop: botão ? + botão X à direita -->
                  <div class="flex-1 flex justify-end items-center gap-2">
                    <div
                      v-if="!isMobile"
                      class="relative"
                    >
                      <button
                        ref="infoBtnRef"
                        class="flex h-8 w-8 items-center justify-center rounded-full bg-bg-primary !text-[#ffffff] text-lg font-bold transition hover:bg-bg-primary/80"
                        @click.stop="toggleInfo"
                      >
                        ?
                      </button>
                      <!-- Dropdown info desktop -->
                      <div
                        v-if="showInfoDropdown"
                        ref="infoDropdownRef"
                        class="absolute right-0 top-10 z-50 w-[320px] rounded-xl p-4 text-sm text-white leading-relaxed shadow-lg bg-bg-primary border border-white/10"
                      >
                        <p
                          >A cada 1 hora, você ganha uma nova chance de girar a
                          roleta!</p
                        >
                        <p class="mt-2">
                          Cada giro sorteia um número aleatório — quanto maior o
                          número, melhor a sua colocação no ranking.
                        </p>
                        <p class="mt-2">
                          Ao final de cada hora, os 10 maiores números do
                          período são os vencedores do prêmio 🎁
                        </p>
                      </div>
                    </div>
                    <button
                      class="flex h-8 w-8 items-center justify-center rounded-full bg-bg-primary !text-[#ffffff] transition hover:bg-bg-primary/80"
                      @click="handleClose"
                    >
                      <NuxtIcon
                        class="text-lg"
                        name="close"
                      />
                    </button>
                  </div>
                </div>

                <!-- Frame com logo, números e botão -->
                <div class="relative flex flex-col items-center mb-0.5">
                  <!-- Blue glow behind frame -->
                  <div
                    class="absolute pointer-events-none z-0"
                    style="
                      width: 200%;
                      left: -50%;
                      bottom: -10%;
                      height: 110%;
                      background:
                        radial-gradient(
                          ellipse 40% 55% at 50% 55%,
                          rgba(255, 255, 255, 0.7) 0%,
                          rgba(255, 255, 255, 0.15) 30%,
                          transparent 55%
                        ),
                        radial-gradient(
                          ellipse 55% 70% at 50% 55%,
                          rgba(56, 189, 248, 1) 0%,
                          rgba(37, 99, 235, 0.85) 30%,
                          rgba(29, 78, 216, 0.4) 55%,
                          transparent 75%
                        );
                      filter: blur(32px);
                    "
                  />
                  <!-- Frame (já inclui logo GIRATUDO) + números -->
                  <div class="relative z-10 w-[320px] lg:w-[480px]">
                    <img
                      alt="Giratudo frame"
                      class="w-full h-auto pointer-events-none select-none"
                      draggable="false"
                      :src="frameUrl"
                    />

                    <!-- Números posicionados dentro do frame -->
                    <div
                      class="absolute inset-0 flex items-center justify-center gap-1 lg:gap-2 pt-[10%]"
                      :class="[showCelebration ? 'giratudo-pulse' : '']"
                      :style="{ gap: showCelebration ? '12px' : undefined }"
                    >
                      <span
                        v-for="(digit, i) in digits"
                        :key="i"
                        class="giratudo-digit"
                        :class="{
                          'giratudo-digit--landed': landed[i],
                          'giratudo-digit--spinning': isSpinning && !landed[i],
                        }"
                      >
                        <span
                          class="inline-flex items-center justify-center font-black text-[3.5rem] lg:text-[5.5rem] giratudo-digit__text"
                        >
                          {{ digit }}
                        </span>
                      </span>
                    </div>
                    <!-- Lottie celebration overlay -->
                    <Transition
                      enter-active-class="transition-opacity duration-300"
                      enter-from-class="opacity-0"
                      enter-to-class="opacity-100"
                      leave-active-class="transition-opacity duration-700"
                      leave-from-class="opacity-100"
                      leave-to-class="opacity-0"
                    >
                      <Vue3Lottie
                        v-if="showCelebration"
                        :animation-data="celebrationAnimation"
                        :auto-play="true"
                        class="absolute inset-0 pointer-events-none z-10"
                        :loop="false"
                        :speed="0.7"
                      />
                    </Transition>
                  </div>

                  <!-- Botão GIRAR ROLETA encaixado na sombra inferior do frame -->
                  <button
                    class="relative z-10 mt-[10px] lg:mt-[10px] mb-0.5 transition active:scale-95 disabled:cursor-not-allowed"
                    :class="[
                      isSpinning || statusLoading
                        ? 'cursor-wait'
                        : !raffleActive || alreadyParticipated
                          ? ''
                          : 'hover:brightness-110 cursor-pointer',
                    ]"
                    :disabled="
                      !raffleActive ||
                      !participationEnabled ||
                      (eligible
                        ? isSpinning || statusLoading || alreadyParticipated
                        : alreadyParticipated)
                    "
                    @click="
                      raffleActive && !eligible && !alreadyParticipated
                        ? handleFirstDeposit()
                        : handleSpin()
                    "
                    @pointerdown="touchAudioContext"
                    @touchstart.passive="touchAudioContext"
                  >
                    <img
                      alt="Girar Roleta"
                      class="w-[192px] lg:w-[264px] h-auto pointer-events-none select-none"
                      draggable="false"
                      :src="
                        raffleActive && !eligible && !alreadyParticipated
                          ? btnFirstDepositUrl
                          : isSpinning ||
                              statusLoading ||
                              !raffleActive ||
                              !participationEnabled ||
                              !eligible ||
                              alreadyParticipated
                            ? btnGirarOffUrl
                            : btnGirarUrl
                      "
                    />
                  </button>
                  <!-- Feedback de estado abaixo do botão (espaço fixo para não pular layout) -->
                  <div class="mt-1 min-h-[1.25rem]">
                    <p
                      v-if="!participationEnabled && !statusLoading"
                      class="text-[#ffffff]/60 text-xs font-bold text-center"
                    >
                      Sorteios temporariamente indisponíveis. Volte em breve.
                    </p>
                    <p
                      v-else-if="alreadyParticipated"
                      class="text-[#62eafb] text-xs font-bold text-center"
                    >
                      Você já participou deste ciclo!
                    </p>
                    <p
                      v-else-if="!raffleActive && !statusLoading"
                      class="text-[#ffffff]/60 text-xs font-bold text-center"
                    >
                      Nenhum sorteio ativo no momento.
                    </p>
                    <p
                      v-else-if="raffleActive && !eligible && !statusLoading"
                      class="text-[#ffffff]/60 text-xs font-bold text-center"
                    >
                      Faça um depósito para participar.
                    </p>
                    <p
                      v-else-if="participateError"
                      class="text-red-400 text-xs font-bold text-center"
                    >
                      {{ participateError }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Tables Section (scroll apenas dentro de cada tabela) -->
            <div
              ref="tablesSectionRef"
              class="overflow-hidden"
              :style="
                isDesktop
                  ? {
                      height: `${TABLE_MIN_HEIGHT * desktopTableScale}px`,
                      transformOrigin: 'top center',
                    }
                  : { flex: '1 1 0%', minHeight: 0, maxHeight: '630px' }
              "
            >
              <!-- Desktop: 3 columns side by side -->
              <div
                v-if="isDesktop"
                class="grid grid-cols-3 items-stretch gap-4 px-4 pb-4"
                :style="{
                  height: `${TABLE_MIN_HEIGHT}px`,
                  transform: `scale(${desktopTableScale})`,
                  transformOrigin: 'top center',
                }"
              >
                <GiratudoTablePlacar
                  :data="placarData"
                  :loading="placarLoading"
                  :page="placarPage"
                  title="Placar"
                  :total-pages="placarTotalPages"
                  :user-position="placarUserPosition"
                  @update:page="placarPage = $event"
                />

                <GiratudoTableMeusPremios
                  :data="meusPremiosData"
                  :loading="meusPremiosLoading"
                  :page="meusPremiosPage"
                  title="Meus prêmios"
                  :total-pages="meusPremiosTotalPages"
                  @update:page="meusPremiosPage = $event"
                />

                <GiratudoTableHistorico
                  :data="historicoData"
                  :loading="historicoLoading"
                  :page="historicoPage"
                  title="Histórico"
                  :total-pages="historicoTotalPages"
                  @update:page="historicoPage = $event"
                />
              </div>

              <!-- Mobile: Tabs -->
              <div
                v-else
                class="flex flex-col h-full min-h-0 px-3 pb-2"
              >
                <!-- Tab Headers -->
                <div class="shrink-0 flex gap-1.5 pt-1.5">
                  <button
                    v-for="tab in tabs"
                    :key="tab.id"
                    class="flex-1 px-3 py-[10px] transition"
                    :style="
                      activeTab === tab.id
                        ? 'border-radius: 10px 10px 0 0; background: linear-gradient(180deg, #0143e3 0%, #000717 100%); color: #ffffff; font-family: \'Plus Jakarta Sans\', sans-serif; font-size: 12.433px; font-weight: 700; line-height: 12.433px;'
                        : 'border-radius: 10px 10px 0 0; background: linear-gradient(180deg, #121C35 0%, #000717 100%); backdrop-filter: blur(5px); color: #536A9E; font-family: \'Plus Jakarta Sans\', sans-serif; font-size: 12.433px; font-weight: 700; line-height: 12.433px;'
                    "
                    @click="activeTab = tab.id"
                  >
                    {{ tab.label }}
                  </button>
                </div>

                <!-- Tab Content -->
                <div class="flex-1 min-h-0">
                  <GiratudoTablePlacar
                    v-if="activeTab === 'placar'"
                    class="h-full"
                    :data="placarData"
                    :loading="placarLoading"
                    :page="placarPage"
                    :total-pages="placarTotalPages"
                    :user-position="placarUserPosition"
                    @update:page="placarPage = $event"
                  />
                  <GiratudoTableMeusPremios
                    v-else-if="activeTab === 'meus-premios'"
                    class="h-full"
                    :data="meusPremiosData"
                    :loading="meusPremiosLoading"
                    :page="meusPremiosPage"
                    :total-pages="meusPremiosTotalPages"
                    @update:page="meusPremiosPage = $event"
                  />
                  <GiratudoTableHistorico
                    v-else
                    class="h-full"
                    :data="historicoData"
                    :loading="historicoLoading"
                    :page="historicoPage"
                    :total-pages="historicoTotalPages"
                    @update:page="historicoPage = $event"
                  />
                </div>
              </div>
            </div>
          </div>
        </HeadlessTransitionChild>
      </div>
    </HeadlessTransitionRoot>
  </Teleport>
</template>

<style scoped>
.giratudo-pulse {
  transition: gap 600ms ease-in-out;
}

.maintenance-icon {
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.7));
  animation: maintenance-pulse 2s ease-in-out infinite;
}

@keyframes maintenance-pulse {
  0%,
  100% {
    opacity: 0.85;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
}

.giratudo-digit {
  filter: drop-shadow(0 0 6px rgba(98, 234, 251, 0.4))
    drop-shadow(0 2px 4px rgba(0, 0, 0, 0.7));
  transition:
    filter 0.3s ease,
    transform 0.3s ease;
}

.giratudo-digit__text {
  background: linear-gradient(
    180deg,
    #ffffff 0%,
    #e0eaff 30%,
    #62eafb 50%,
    #a8d4f0 70%,
    #ffffff 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 900;
}

/* Dígito ainda não revelado — mais apagado */
.giratudo-digit--spinning {
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.7));
  opacity: 0.5;
}

/* Punch + glow quando o dígito para no número final */
.giratudo-digit--landed {
  animation: digit-land 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  filter: drop-shadow(0 0 12px rgba(98, 234, 251, 0.8))
    drop-shadow(0 0 24px rgba(98, 234, 251, 0.4))
    drop-shadow(0 2px 4px rgba(0, 0, 0, 0.7));
}

@keyframes digit-land {
  0% {
    transform: scale(1);
    opacity: 0.5;
  }
  50% {
    transform: scale(1.2);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
